import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isSuperAdmin } from "@/lib/permissions";
import { useConfigModules } from "@/features/configuration/api";
import {
  ApprovalRulesTab, FormFieldsTab, MasterDataTab, NotificationRulesTab,
  SlaRulesTab, StatusesTab, WorkflowsTab,
} from "@/features/configuration/tabs";
import { CodeGenerationTab } from "@/features/configuration/CodeGenerationTab";
import { AuditLogTab } from "@/features/configuration/AuditLogTab";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute("/_authenticated/configuration")({
  component: () => (
    <RouteGuard module="configuration"><ConfigurationCentre /></RouteGuard>
  ),
});

function ConfigurationCentre() {
  const { data: me } = useCurrentUser();
  const readOnly = !isSuperAdmin(me?.roles ?? []);
  const { data: modules, isLoading: modulesLoading, error: modulesError } = useConfigModules();
  const [moduleKey, setModuleKey] = useState<string>("");
  const [tab, setTab] = useState<string>("master-data");

  // Default selected module: work_orders if present, else first active.
  useEffect(() => {
    if (!moduleKey && modules && modules.length > 0) {
      const preferred = modules.find((m) => m.module_key === "work_orders");
      setModuleKey((preferred ?? modules[0]).module_key);
    }
  }, [moduleKey, modules]);

  return (
    <div className="space-y-6 p-4 md:p-8 pb-16">
      <div className="space-y-0.5">
        <h2 className="text-2xl font-bold tracking-tight">Configuration Centre</h2>
        <p className="text-muted-foreground">Manage global settings and module-specific configurations.</p>
      </div>

      {readOnly && (
        <Alert className="max-w-4xl">
          <AlertDescription>You have read-only access. Only Super Admin can modify configuration.</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col space-y-8 lg:flex-row lg:items-start lg:space-x-8 lg:space-y-0">
        {/* SIDEBAR */}
        <aside className="lg:w-1/5 lg:min-w-[260px] lg:sticky lg:top-6 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto scrollbar-none">
          <nav className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-8">
            
            {/* Global Group */}
            <div>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Global Settings</h4>
              <div className="space-y-1">
                <Button variant={tab === "code-generation" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("code-generation")}>Code Generation</Button>
                <Button variant={tab === "audit" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("audit")}>Audit Log</Button>
              </div>
            </div>

            {/* Module Group */}
            <div>
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Module Settings</h4>
              <div className="mb-3">
                <Select value={moduleKey} onValueChange={setModuleKey} disabled={modulesLoading || !modules?.length}>
                  <SelectTrigger className="w-full bg-transparent">
                    <SelectValue placeholder={modulesLoading ? "Loading modules..." : "Select module"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(modules ?? []).map((m) => (
                      <SelectItem key={m.module_key} value={m.module_key}>{m.module_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {modulesError && (
                  <p className="mt-1.5 text-xs text-destructive">Unable to load modules.</p>
                )}
                {!modulesLoading && !modulesError && modules && modules.length === 0 && (
                  <p className="mt-1.5 text-xs text-muted-foreground">No modules found.</p>
                )}
              </div>

              <div className="space-y-1">
                <Button variant={tab === "master-data" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("master-data")}>Master Data</Button>
                <Button variant={tab === "statuses" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("statuses")}>Statuses</Button>
                <Button variant={tab === "workflows" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("workflows")}>Workflows</Button>
                <Button variant={tab === "approvals" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("approvals")}>Approval Rules</Button>
                {moduleKey === "tickets" && (
                  <Button variant={tab === "sla" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("sla")}>SLA Rules</Button>
                )}
                <Button variant={tab === "notifications" ? "secondary" : "ghost"} className="w-full justify-start font-medium" onClick={() => setTab("notifications")}>Notifications</Button>
              </div>
            </div>

          </nav>
        </aside>

        {/* CONTENT AREA */}
        <div className="flex-1 min-w-0 lg:max-w-5xl">
          {tab === "master-data" && <MasterDataTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "code-generation" && <CodeGenerationTab readOnly={readOnly} />}
          {tab === "statuses" && <StatusesTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "workflows" && <WorkflowsTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "approvals" && <ApprovalRulesTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "sla" && moduleKey === "tickets" && <SlaRulesTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "notifications" && <NotificationRulesTab moduleKey={moduleKey} readOnly={readOnly} />}
          {tab === "audit" && <AuditLogTab />}
        </div>
      </div>
    </div>
  );
}
